import React, { useState, useMemo } from 'react';
import styles from './ResponsiveTable.module.css';

/**
 * ResponsiveTable Component
 * 
 * Soluciona problemas de empalmes en tablas con muchas columnas
 * Características:
 * - Scroll horizontal cuando hay muchas columnas
 * - Columnas prioritarias siempre visibles
 * - Reordenación automática en zoom alto (>125%)
 * - Modo compacto en pantallas pequeñas
 */
function ResponsiveTable({ 
  columns = [], 
  data = [], 
  onRowClick = null,
  renderCell = null,
  className = '',
  compact = false 
}) {
  const [isCompactMode, setIsCompactMode] = useState(false);

  // Detectar zoom actual del navegador
  const currentZoom = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.devicePixelRatio || 1;
    }
    return 1;
  }, []);

  // Determinar si necesita scroll horizontal
  const needsHorizontalScroll = useMemo(() => {
    const totalColumns = columns.length;
    // Más de 8 columnas o zoom > 125% requiere scroll
    return totalColumns > 8 || currentZoom > 1.25;
  }, [columns.length, currentZoom]);

  // Filtrar columnas según zoom
  const visibleColumns = useMemo(() => {
    if (currentZoom <= 1.25 || !isCompactMode) {
      return columns;
    }
    
    // En zoom alto, mostrar solo columnas esenciales
    return columns.filter(col => col.priority !== 'hidden');
  }, [columns, currentZoom, isCompactMode]);

  const columnCount = visibleColumns.length;

  return (
    <div className={`${styles.responsiveTableWrapper} ${className}`}>
      {needsHorizontalScroll && (
        <div className={styles.scrollHint}>
          ← Desliza para ver más columnas →
        </div>
      )}

      <div className={`${styles.tableContainer} ${needsHorizontalScroll ? styles.withScroll : ''}`}>
        <table className={`${styles.responsiveTable} ${compact ? styles.compact : ''}`}>
          <thead>
            <tr>
              {visibleColumns.map((col, idx) => (
                <th 
                  key={col.key || idx}
                  className={`${styles.tableHeader} ${col.priority === 'high' ? styles.priorityHigh : ''}`}
                  style={{
                    width: col.width || 'auto',
                    minWidth: col.minWidth || '100px',
                    textAlign: col.align || 'left'
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, rowIdx) => (
                <tr
                  key={row.id || rowIdx}
                  className={`${styles.tableRow} ${rowIdx % 2 === 0 ? styles.evenRow : styles.oddRow}`}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {visibleColumns.map((col, colIdx) => (
                    <td
                      key={`${rowIdx}-${col.key || colIdx}`}
                      className={`${styles.tableCell} ${col.priority === 'high' ? styles.priorityHigh : ''}`}
                      style={{
                        width: col.width || 'auto',
                        minWidth: col.minWidth || '100px',
                        textAlign: col.align || 'left',
                        whiteSpace: col.nowrap ? 'nowrap' : 'normal'
                      }}
                    >
                      {renderCell ? (
                        renderCell(row, col, rowIdx)
                      ) : (
                        row[col.key] || '—'
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columnCount} className={styles.noData}>
                  No hay datos disponibles
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {currentZoom > 1.25 && (
        <div className={styles.zoomWarning}>
          ℹ️ Zoom detectado: {Math.round(currentZoom * 100)}% - La tabla puede mostrar scroll horizontal
        </div>
      )}
    </div>
  );
}

export default ResponsiveTable;
